import { useEffect, useState } from "react";
import axios from "axios";
import { Inbox, CalendarCheck, Wrench, Star, CheckCircle2, Sparkles, Car, KeyRound, Tag, Users, HandCoins } from "lucide-react";
import { toast } from "sonner";
import { Send, FileSpreadsheet } from "lucide-react";
import { API, auth, handleAuthError, ghostBtn } from "./shared";
import AdminPayments from "./AdminPayments";

const MONTHS = ["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil", "Aoû", "Sep", "Oct", "Nov", "Déc"];
const label = (ym) => `${MONTHS[Number(ym.slice(5, 7)) - 1]} ${ym.slice(2, 4)}`;

const Kpi = ({ icon: Icon, value, text, orange, testid }) => (
  <div className="rounded-3xl border border-brand/10 bg-white p-6" data-testid={testid}>
    <span className={`flex h-11 w-11 items-center justify-center rounded-xl text-white ${orange ? "bg-gold" : "bg-brand"}`}>
      <Icon className="h-5 w-5" />
    </span>
    <p className="font-display mt-4 text-3xl font-extrabold text-forest">{value}</p>
    <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-brand">{text}</p>
  </div>
);

const AdminStats = ({ token, logout }) => {
  const [s, setS] = useState(null);

  useEffect(() => {
    axios.get(`${API}/stats`, auth(token)).then((r) => setS(r.data)).catch((err) => handleAuthError(err, logout, () => {}));
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!s) return <p className="text-sm text-brand">Chargement...</p>;
  const maxMonth = Math.max(1, ...s.per_month.map((m) => m.count));
  const maxService = Math.max(1, ...s.top_services.map((x) => x.count));

  const sendReport = async () => {
    try {
      const r = await axios.post(`${API}/reports/monthly/send`, {}, auth(token));
      toast.success(`Rapport ${r.data.month} envoyé au directeur (CA facturé ${Math.round(r.data.revenue).toLocaleString("fr-FR")} FCFA)`);
    } catch (err) {
      handleAuthError(err, logout, () => toast.error("Échec de l'envoi du rapport"));
    }
  };

  const exportFull = async () => {
    try {
      const res = await axios.get(`${API}/export/full`, { ...auth(token), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `groupe-auto-luxe-comptabilite-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Classeur Excel complet téléchargé (Devis, Clients, Factures, Stock)");
    } catch (err) {
      handleAuthError(err, logout, () => toast.error("Échec de l'export"));
    }
  };

  return (
    <div data-testid="admin-stats-tab">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-mint p-5 text-sm text-forest" data-testid="report-banner">
        <span>📊 Un rapport mensuel (devis, factures, chiffre d'affaires, véhicules vendus / loués, stock bas) est envoyé automatiquement au directeur le 1er de chaque mois à 8h.</span>
        <span className="flex flex-wrap gap-2"><button onClick={sendReport} data-testid="report-send-now" className={ghostBtn}><Send className="h-3.5 w-3.5" /> Envoyer le rapport du mois maintenant</button><button onClick={exportFull} data-testid="export-full-button" className="inline-flex items-center gap-2 rounded-full bg-brand px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-white hover:bg-brand-hover"><FileSpreadsheet className="h-3.5 w-3.5" /> Export Excel complet</button></span>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <Kpi icon={Inbox} value={s.total} text="Devis reçus" testid="stat-total" />
        <Kpi icon={Sparkles} value={s.new} text="Nouveaux à traiter" orange testid="stat-new" />
        <Kpi icon={CalendarCheck} value={s.upcoming_appointments} text="Rendez-vous à venir" testid="stat-upcoming" />
        <Kpi icon={Wrench} value={s.in_repair} text="Véhicules à l'atelier" orange testid="stat-in-repair" />
        <Kpi icon={Star} value={s.reviews_avg ?? "—"} text={`Note moyenne (${s.reviews_count} avis)`} testid="stat-reviews" />
      </div>

      <div className="mt-10 grid gap-6 lg:grid-cols-5">
        <div className="rounded-3xl bg-mint p-8 lg:col-span-3" data-testid="stat-per-month">
          <h2 className="font-display text-lg font-bold text-forest">Demandes de devis par mois</h2>
          <p className="mt-1 text-xs text-brand">12 derniers mois</p>
          <div className="mt-8 flex h-56 items-end gap-2">
            {s.per_month.map((m, i) => (
              <div key={m.month} className="group flex flex-1 flex-col items-center gap-2" data-testid={`stat-month-${i}`}>
                <span className="font-mono text-[11px] font-bold text-forest">{m.count}</span>
                <div
                  className={`w-full rounded-t-xl transition-all duration-700 ${i === s.per_month.length - 1 ? "bg-gold" : "bg-brand"} group-hover:opacity-80`}
                  style={{ height: `${Math.max(4, (m.count / maxMonth) * 160)}px` }}
                />
                <span className="font-mono text-[9px] uppercase text-brand">{label(m.month)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-3xl border border-brand/10 bg-white p-8 lg:col-span-2" data-testid="stat-top-services">
          <h2 className="font-display text-lg font-bold text-forest">Services les plus demandés</h2>
          <ul className="mt-6 space-y-4">
            {s.top_services.map((x, i) => (
              <li key={x.service} data-testid={`stat-service-${i}`}>
                <div className="flex items-center justify-between gap-3 text-sm">
                  <span className="truncate font-semibold text-forest">{x.service}</span>
                  <span className="font-mono text-xs text-brand">{x.count}</span>
                </div>
                <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-mint">
                  <div className={`h-full rounded-full ${i === 0 ? "bg-gold" : "bg-brand"}`} style={{ width: `${(x.count / maxService) * 100}%` }} />
                </div>
              </li>
            ))}
            {s.top_services.length === 0 && <li className="text-sm text-brand">Aucune demande pour le moment.</li>}
          </ul>
          <p className="mt-6 flex items-center gap-2 text-xs text-brand">
            <CheckCircle2 className="h-4 w-4" /> {s.handled} demande(s) traitée(s) sur {s.total}
          </p>
        </div>
      </div>

      {s.vehicles && (
        <section className="mt-10 rounded-3xl border border-brand/10 bg-white p-8" data-testid="stat-vehicles">
          <h2 className="font-display flex items-center gap-2 text-lg font-bold text-forest"><Car className="h-5 w-5 text-gold" /> Véhicules et pièces : vente, achat et location</h2>
          <p className="mt-1 text-xs text-brand">Annonces en ligne et activité cumulée. Le détail du mois figure dans le rapport mensuel.</p>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <Kpi icon={Tag} value={s.vehicles.published_sale} text="Annonces à vendre en ligne" testid="stat-vehicles-sale" />
            <Kpi icon={KeyRound} value={s.vehicles.published_rent} text="Annonces à louer en ligne" testid="stat-vehicles-rent" />
            <Kpi icon={HandCoins} value={s.vehicles.sold} text={`Vendus (${Math.round(s.vehicles.sold_total).toLocaleString("fr-FR")} FCFA)`} orange testid="stat-vehicles-sold" />
            <Kpi icon={Car} value={s.vehicles.rented} text={`Loués · ${s.vehicles.reserved} réservé(s)`} testid="stat-vehicles-rented" />
            <Kpi icon={Users} value={s.vehicles.leads} text={`Clients intéressés (${s.vehicles.leads_new} nouveaux) · ${s.vehicles.offers} proposition(s) d'achat`} orange testid="stat-vehicles-leads" />
          </div>
          <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="stat-parts">
            <Kpi icon={Wrench} value={s.vehicles.parts_sale_online} text="Pièces à vendre en ligne" testid="stat-parts-sale" />
            <Kpi icon={Wrench} value={s.vehicles.parts_wanted_online} text="Pièces recherchées en ligne" orange testid="stat-parts-wanted" />
            <Kpi icon={HandCoins} value={s.vehicles.parts_sold} text={`Pièces vendues (${Math.round(s.vehicles.parts_sold_total).toLocaleString("fr-FR")} FCFA)`} testid="stat-parts-sold" />
            <Kpi icon={Users} value={s.vehicles.parts_found} text={`Pièces recherchées trouvées · ${s.vehicles.part_leads} réponse(s) clients`} orange testid="stat-parts-found" />
          </div>
          {s.vehicles.top_wanted.length > 0 && (
            <ul className="mt-6 flex flex-wrap gap-2" data-testid="stat-vehicles-wanted">
              {s.vehicles.top_wanted.map((w) => <li key={w.title} className="rounded-full bg-mint px-3 py-1.5 text-xs text-forest">{w.title} <b className="text-gold">· {w.count}</b></li>)}
            </ul>
          )}
        </section>
      )}

      <AdminPayments token={token} logout={logout} />
    </div>
  );
};

export default AdminStats;
